import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Truck, Plus, Search, Pen, Trash2, X as IconX, Phone, Mail, MapPin, FileText, CreditCard, User, CheckCircle2, XCircle, Power } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomSelect } from '../../components/CustomSelect';
import {
  suppliersOf, addSupplier, updateSupplier, deleteSupplier,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { formatCategoryName, formatName, formatPhone, formatGST } from '../../utils/formatters';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

const PAYMENT_TERMS = [
  { value: 'Immediate', label: 'Immediate' },
  { value: 'Net 30', label: 'Net 30 days' },
  { value: 'Net 60', label: 'Net 60 days' },
];

const blank = (orgId) => ({
  id: null,
  orgId,
  supplierName: '',
  contactPerson: '',
  mobileNumber: '',
  email: '',
  gstNumber: '',
  address: '',
  paymentTerms: 'Immediate',
  status: 'Active',
});

const Field = ({ label, children, className = '' }) => (
  <label className={`block space-y-1 ${className}`}>
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
    {children}
  </label>
);
Field.propTypes = { label: PropTypes.node.isRequired, children: PropTypes.node.isRequired, className: PropTypes.string };

const inputCls = (err) =>
  `w-full rounded-lg bg-gray-50 border ${err ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`;

const Modal = ({ open, draft, setDraft, onClose, onSubmit, mode, accent, showNotification }) => {
  const [errors, setErrors] = useState({});

  useEffect(() => { if (!open) setErrors({}); }, [open]);
  if (!open) return null;

  const set = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });

  const handleSubmit = () => {
    const errs = {};
    if (!draft.supplierName.trim()) errs.supplierName = 'Supplier name is required.';
    if (!draft.mobileNumber || draft.mobileNumber.replace(/\D/g, '').length < 10) errs.mobileNumber = 'Valid 10-digit mobile required.';
    if (draft.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email)) errs.email = 'Valid email format required.';
    if (draft.gstNumber && draft.gstNumber.trim().length !== 15) errs.gstNumber = 'Valid 15-character GSTIN required.';
    if (Object.keys(errs).length) {
      setErrors(errs);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col max-h-[90dvh]"
        >
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}>
                <Truck size={18} />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit supplier' : 'New supplier'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Organisation-wide master</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
              <IconX size={16} />
            </button>
          </header>

          <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-4">
            {/* Required */}
            <Field label={<span>Supplier Name <span className="text-rose-500 ml-1">*</span></span>}>
              <input type="text" value={draft.supplierName} onChange={(e) => {
                setDraft({ ...draft, supplierName: formatCategoryName(e.target.value) });
                if (errors.supplierName) setErrors({ ...errors, supplierName: undefined });
              }}
                placeholder="e.g. Sri Krishna Traders" className={inputCls(errors.supplierName)} />
              {errors.supplierName && <p className="text-[11px] font-medium text-rose-500">{errors.supplierName}</p>}
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Contact Person">
                <input type="text" value={draft.contactPerson} onChange={(e) => setDraft({ ...draft, contactPerson: formatName(e.target.value) })}
                  placeholder="e.g. Ramesh Kumar" className={inputCls()} />
              </Field>
              <Field label={<span>Mobile Number <span className="text-rose-500 ml-1">*</span></span>}>
                <div className="relative">
                  <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="tel" value={draft.mobileNumber || ''} onChange={(e) => { setDraft({ ...draft, mobileNumber: formatPhone(e.target.value) }); if (errors.mobileNumber) setErrors({ ...errors, mobileNumber: undefined }); }}
                    placeholder="9876543210" className={`${inputCls(errors.mobileNumber)} pl-8`} />
                </div>
                {errors.mobileNumber && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.mobileNumber}</p>}
              </Field>
              <Field label="Email">
                <div className="relative">
                  <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="email" value={draft.email} onChange={(e) => { setDraft({ ...draft, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: undefined }); }}
                    placeholder="vendor@example.com" className={`${inputCls(errors.email)} pl-8`} />
                </div>
                {errors.email && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.email}</p>}
              </Field>
              <Field label="GSTIN">
                <div className="relative">
                  <FileText size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={draft.gstNumber} onChange={(e) => { setDraft({ ...draft, gstNumber: formatGST(e.target.value) }); if (errors.gstNumber) setErrors({ ...errors, gstNumber: undefined }); }}
                    placeholder="22AAAAA0000A1Z5" className={`${inputCls(errors.gstNumber)} pl-8 uppercase`} />
                </div>
                {errors.gstNumber && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.gstNumber}</p>}
              </Field>
            </div>

            <Field label="Address">
              <div className="relative">
                <MapPin size={13} className="absolute left-3 top-3 text-slate-400" />
                <textarea value={draft.address} onChange={set('address')}
                  placeholder="Street, City, State – PIN" rows={3}
                  className={`${inputCls()} pl-8 resize-none`} />
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Payment Terms">
                <CustomSelect
                  value={draft.paymentTerms}
                  onChange={(v) => setDraft({ ...draft, paymentTerms: v })}
                  options={PAYMENT_TERMS}
                  className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2.5 px-3"
                />
              </Field>
              <Field label="Status">
                <CustomSelect
                  value={draft.status}
                  onChange={(v) => setDraft({ ...draft, status: v })}
                  options={[
                    { value: 'Active', label: 'Active', icon: <Power size={14} className="text-emerald-500" /> },
                    { value: 'Inactive', label: 'Inactive', icon: <Power size={14} className="text-rose-500" /> }
                  ]}
                  className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2.5 px-3"
                />
              </Field>
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="button" onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
              style={{ background: accent }}>
              {mode === 'edit' ? 'Save changes' : 'Create supplier'}
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

const termColor = (term) => {
  if (term === 'Immediate') return { bg: 'rgba(16,185,129,0.10)', fg: '#047857' };
  if (term === 'Net 30') return { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' };
  return { bg: 'rgba(99,102,241,0.10)', fg: '#4338ca' };
};

export const SuppliersTab = ({ org, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const list = useMemo(() => {
    const all = suppliersOf(org.id);
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((s) =>
      (s.supplierName || '').toLowerCase().includes(q) ||
      (s.contactPerson || '').toLowerCase().includes(q) ||
      (s.mobileNumber || '').includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    );
  }, [org.id, search, version, dataVersion]);

  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank(org.id)); setMode('create'); setModalOpen(true); };
  const openEdit = (rec) => { setDraft({ ...rec }); setMode('edit'); setModalOpen(true); };

  const submit = (record) => {
    const final = { ...record, orgId: org.id };
    if (mode === 'create') addSupplier(final);
    else updateSupplier(record.id, final);
    if (showNotification) showNotification(`${mode === 'create' ? 'Created' : 'Updated'} ${final.supplierName}`, 'success');
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteSupplier(confirmDelete.id);
    if (showNotification) showNotification(`Removed ${confirmDelete.supplierName}`, 'success');
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  const toggleStatus = (s) => {
    const newStatus = s.status === 'Active' ? 'Inactive' : 'Active';
    updateSupplier(s.id, { ...s, status: newStatus });
    if (showNotification) showNotification(`${s.supplierName} is now ${newStatus}`, 'success');
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search suppliers…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors" />
          </div>
          </div>
        <button type="button" onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
          <Plus size={14} /> New supplier
        </button>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg h-fit">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[var(--theme-tertiary)] bg-gray-50/50">
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%] align-middle">Supplier Name</th>
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[12%] align-middle">Mobile</th>
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[18%] align-middle">Email</th>
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%] align-middle">GSTIN</th>
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[10%] align-middle">Terms</th>
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[10%] align-middle">Status</th>
              <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[15%] align-middle">Actions</th>
            </tr>
          </thead>
            <tbody>
              {list.map((s) => {
                const tc = termColor(s.paymentTerms);
                return (
                  <tr key={s.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3 align-middle">
                      <p className="text-[13px] font-extrabold text-[#0d0d0d]">{s.supplierName}</p>
                    </td>
                    <td className="px-5 py-3 align-middle">
                      <p className="text-[12px] font-medium text-slate-700">{s.mobileNumber || '—'}</p>
                    </td>
                    <td className="px-5 py-3 align-middle">
                      <p className="text-[12px] font-medium text-slate-500 truncate" title={s.email}>{s.email || '—'}</p>
                    </td>
                    <td className="px-5 py-3 align-middle">
                      <p className="text-[12px] font-medium text-slate-700">{s.gstNumber || '—'}</p>
                    </td>
                    <td className="px-5 py-3 align-middle">
                      <span className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: tc.fg }}>
                        {s.paymentTerms || 'Immediate'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center align-middle">
                      <StatusToggle
                        on={(s.status || 'Active') === 'Active'}
                        label={s.name}
                        onChange={() => toggleStatus(s)}
                      />
                    </td>
                    <td className="px-5 py-3 text-right align-middle">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${s.name}`} title="Edit" onClick={() => openEdit(s)} />
                        <IconAction icon={Trash2} tone="danger" label={`Remove ${s.name}`} title="Remove" onClick={() => setConfirmDelete(s)} />
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center">
                    <Truck size={28} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No suppliers match.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

      <Modal open={modalOpen} draft={draft} setDraft={setDraft}
        onClose={() => setModalOpen(false)} onSubmit={submit} mode={mode} accent={accent} showNotification={showNotification} />

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3"
                style={{ background: 'rgba(244,63,94,0.10)' }}>
                <Trash2 size={20} />
              </div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.supplierName}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">
                Past purchases that used this supplier name will not be affected.
              </p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)}
                className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete}
                className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

SuppliersTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
